import { createCapSolver } from "./modules/capChallenge";
import {
  getPhoneCountry,
  getPhoneNumber,
  initIntlPhone,
  isPhoneNumberValid,
} from "./modules/dynamicContentLoader";
import {
  clearSignature,
  getSignatureDataUrl,
  initSignaturePad,
  isSignatureValid,
} from "./modules/signaturePadModule";

(function () {
  const root = document.getElementById("media-consent-root");
  if (!root) {
    return;
  }

  const form = document.getElementById("media-consent-form");
  const nameInput = document.getElementById("media-consent-full-name");
  const phoneInput = document.getElementById("media-consent-phone");
  const emailInput = document.getElementById("media-consent-email");
  const ageInput = document.getElementById("media-consent-age");
  const agreeInput = document.getElementById("media-consent-agree");
  const submitButton = document.getElementById("media-consent-submit");
  const clearButton = document.getElementById("media-consent-clear-signature");
  const submitSpinner = document.getElementById("media-consent-submit-spinner");
  const submitText = document.getElementById("media-consent-submit-text");
  const dateEl = document.getElementById("media-consent-date");
  const formFields = document.getElementById("media-consent-form-fields");
  const confirmationPanel = document.getElementById("media-consent-confirmation");
  const confirmationNameEl = document.getElementById("media-consent-confirmation-name");
  const confirmationMessageEl = document.getElementById("media-consent-confirmation-message");
  const submitAnotherButton = document.getElementById("media-consent-submit-another");
  const localListPanel = document.getElementById("media-consent-local-panel");
  const localListCountEl = document.getElementById("media-consent-list-count");
  const localListEmptyEl = document.getElementById("media-consent-list-empty");
  const localListEl = document.getElementById("media-consent-list");
  const clearListButton = document.getElementById("media-consent-clear-list");

  const errorEl = document.getElementById("media-consent-error");
  const nameErrorEl = document.getElementById("media-consent-name-error");
  const phoneErrorEl = document.getElementById("media-consent-phone-error");
  const emailErrorEl = document.getElementById("media-consent-email-error");
  const ageErrorEl = document.getElementById("media-consent-age-error");
  const agreeErrorEl = document.getElementById("media-consent-agree-error");
  const signatureErrorEl = document.getElementById("media-consent-signature-error");

  if (
    !form ||
    !nameInput ||
    !phoneInput ||
    !emailInput ||
    !ageInput ||
    !agreeInput ||
    !submitButton ||
    !clearButton ||
    !formFields ||
    !confirmationPanel ||
    !confirmationNameEl ||
    !confirmationMessageEl ||
    !submitAnotherButton ||
    !localListPanel ||
    !localListCountEl ||
    !localListEmptyEl ||
    !localListEl ||
    !clearListButton
  ) {
    return;
  }

  const text = {
    nameRequired: root.dataset.nameRequired || "",
    phoneRequired: root.dataset.phoneRequired || "",
    invalidPhone: root.dataset.invalidPhone || "",
    invalidEmail: root.dataset.invalidEmail || "",
    ageRequired: root.dataset.ageRequired || "",
    agreementRequired: root.dataset.agreementRequired || "",
    signatureRequired: root.dataset.signatureRequired || "",
    genericError: root.dataset.genericError || "",
    networkError: root.dataset.networkError || "",
    submitLabel: root.dataset.submitLabel || "",
    submittingLabel: root.dataset.submittingLabel || "",
    listEmpty: root.dataset.listEmpty || "",
    listCountSingle: root.dataset.listCountSingle || "",
    listCountMany: root.dataset.listCountMany || "",
    clearListConfirm: root.dataset.clearListConfirm || "",
    submittedLabel: root.dataset.submittedLabel || "",
    recordLabel: root.dataset.recordLabel || "",
    confirmationFor: root.dataset.confirmationFor || "",
    language: root.dataset.language || document.documentElement.lang || "",
  };

  const STORAGE_KEY = "zoniMediaConsentSubmissions:v1";
  const PHONE_INSTANCE_KEY = "itiMediaConsent";
  const capSolver = createCapSolver({
    endpointInputId: "media-consent-cap-api-endpoint",
    tokenInputId: "media-consent-cap-token",
  });

  function formatDateForLanguage(date, options) {
    try {
      return new Intl.DateTimeFormat(text.language || undefined, options)
        .format(date);
    } catch {
      return new Intl.DateTimeFormat(undefined, options).format(date);
    }
  }

  if (dateEl) {
    dateEl.textContent = formatDateForLanguage(new Date(), {
      dateStyle: "medium",
    });
  }

  initSignaturePad(
    "media-consent-signature-pad",
    "media-consent-clear-signature"
  );

  const phoneInitPromise = initIntlPhone({
    selector: "#media-consent-phone",
    instanceKey: PHONE_INSTANCE_KEY,
    errorSelector: "#media-consent-phone-error",
    submitBtnSelector: null,
    preferred: ["us"],
    initialCountry: "us",
    nationalMode: false,
    invalidMessage: text.invalidPhone,
  });

  capSolver.prime();
  getCsrfToken().catch((error) => {
    console.error("Error warming media consent CSRF token:", error);
  });

  function stripHtml(message) {
    const container = document.createElement("div");
    container.innerHTML = String(message || "");
    return (container.textContent || container.innerText || "").trim();
  }

  function safeMessage(message) {
    const clean = stripHtml(message);
    if (!clean) {
      return "";
    }
    if (
      clean.includes("Traceback") ||
      clean.includes("frappe.") ||
      clean.includes("pymysql.") ||
      clean.includes('File "')
    ) {
      return "";
    }
    return clean;
  }

  function extractFrappeErrorMessage(payload) {
    const directMessage = payload?.message;
    if (typeof directMessage === "string" && directMessage.trim()) {
      return safeMessage(directMessage);
    }
    if (directMessage && typeof directMessage === "object") {
      if (typeof directMessage.message === "string") {
        return safeMessage(directMessage.message);
      }
      if (typeof directMessage._error_message === "string") {
        return safeMessage(directMessage._error_message);
      }
    }
    if (typeof payload?._error_message === "string") {
      return safeMessage(payload._error_message);
    }
    if (typeof payload?._server_messages === "string") {
      try {
        const serverMessages = JSON.parse(payload._server_messages);
        for (const entry of serverMessages) {
          if (typeof entry === "string") {
            try {
              const parsed = JSON.parse(entry);
              const clean = safeMessage(parsed?.message);
              if (clean) {
                return clean;
              }
            } catch {
              const clean = safeMessage(entry);
              if (clean) {
                return clean;
              }
            }
          } else if (typeof entry?.message === "string") {
            const clean = safeMessage(entry.message);
            if (clean) {
              return clean;
            }
          }
        }
      } catch {
        return safeMessage(payload._server_messages);
      }
    }
    return "";
  }

  function userSafeError(message) {
    const error = new Error(message || "");
    error.userSafe = true;
    return error;
  }

  function setFieldError(input, el, message) {
    if (el) {
      el.textContent = safeMessage(message);
    }
    if (input) {
      input.classList.toggle("is-invalid", Boolean(message));
    }
  }

  function clearFieldErrors() {
    setFieldError(nameInput, nameErrorEl, "");
    setFieldError(phoneInput, phoneErrorEl, "");
    setFieldError(emailInput, emailErrorEl, "");
    setFieldError(ageInput, ageErrorEl, "");
    setFieldError(agreeInput, agreeErrorEl, "");
    if (signatureErrorEl) {
      signatureErrorEl.textContent = "";
    }
  }

  function showError(message) {
    if (errorEl) {
      errorEl.textContent = safeMessage(message) || text.genericError;
    }
  }

  function clearError() {
    if (errorEl) {
      errorEl.textContent = "";
    }
  }

  function setSubmitting(isSubmitting) {
    submitButton.disabled = isSubmitting;
    clearButton.disabled = isSubmitting;
    submitButton.setAttribute("aria-busy", isSubmitting ? "true" : "false");
    if (submitSpinner) {
      submitSpinner.hidden = !isSubmitting;
    }
    if (submitText) {
      submitText.textContent = isSubmitting
        ? text.submittingLabel
        : text.submitLabel;
    } else {
      submitButton.textContent = isSubmitting
        ? text.submittingLabel
        : text.submitLabel;
    }
  }

  function getLocalStorage() {
    try {
      return window.localStorage || null;
    } catch {
      return null;
    }
  }

  function readLocalSubmissions() {
    const storage = getLocalStorage();
    if (!storage) {
      return [];
    }
    try {
      const parsed = JSON.parse(storage.getItem(STORAGE_KEY) || "[]");
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .filter((item) => item && typeof item === "object")
        .map((item) => ({
          name: String(item.name || "").trim(),
          recordName: String(item.recordName || "").trim(),
          submittedAt: String(item.submittedAt || "").trim(),
        }))
        .filter((item) => item.name);
    } catch {
      return [];
    }
  }

  function writeLocalSubmissions(items) {
    const storage = getLocalStorage();
    if (!storage) {
      return false;
    }
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(items));
      return true;
    } catch {
      return false;
    }
  }

  function formatSubmittedAt(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return formatDateForLanguage(date, {
      dateStyle: "short",
      timeStyle: "short",
    });
  }

  function localCountText(count) {
    if (count === 1) {
      return text.listCountSingle || "1 person submitted on this device.";
    }
    const template = text.listCountMany || "{count} people submitted on this device.";
    return template.replace("{count}", String(count));
  }

  function renderLocalSubmissions() {
    const items = readLocalSubmissions();
    const hasItems = items.length > 0;
    localListPanel.hidden = !hasItems;
    localListEl.replaceChildren();
    localListCountEl.textContent = localCountText(items.length);
    localListEmptyEl.textContent = text.listEmpty;
    localListEmptyEl.hidden = hasItems;
    clearListButton.hidden = !hasItems;

    items.forEach((item) => {
      const li = document.createElement("li");
      const main = document.createElement("div");
      const name = document.createElement("div");
      const meta = document.createElement("div");
      const record = document.createElement("div");

      name.className = "media-consent-list-name";
      name.textContent = item.name;

      meta.className = "media-consent-list-meta";
      const formattedDate = formatSubmittedAt(item.submittedAt);
      meta.textContent = formattedDate
        ? `${text.submittedLabel}: ${formattedDate}`
        : text.submittedLabel;

      record.className = "media-consent-list-record";
      record.textContent = item.recordName
        ? `${text.recordLabel}: ${item.recordName}`
        : "";

      main.append(name, meta);
      li.append(main, record);
      localListEl.append(li);
    });
  }

  function appendLocalSubmission({ name, recordName }) {
    const items = readLocalSubmissions();
    items.push({
      name: String(name || "").trim(),
      recordName: String(recordName || "").trim(),
      submittedAt: new Date().toISOString(),
    });
    writeLocalSubmissions(items);
    renderLocalSubmissions();
  }

  function resetFormForNextSubmission() {
    form.reset();
    clearSignature();
    clearError();
    clearFieldErrors();
    confirmationPanel.hidden = true;
    formFields.hidden = false;
    requestAnimationFrame(() => {
      initSignaturePad(
        "media-consent-signature-pad",
        "media-consent-clear-signature"
      );
      nameInput.focus();
    });
  }

  function showConfirmation({ name, message }) {
    confirmationNameEl.textContent = text.confirmationFor
      ? `${text.confirmationFor}: ${name}`
      : name;
    confirmationMessageEl.textContent = safeMessage(message);
    formFields.hidden = true;
    confirmationPanel.hidden = false;
    form.scrollIntoView({ behavior: "smooth", block: "start" });
    submitAnotherButton.focus({ preventScroll: true });
  }

  function isValidEmailSyntax(value) {
    const trimmed = String(value || "").trim();
    if (!trimmed) {
      return true;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  }

  async function validateBeforeSubmit() {
    clearFieldErrors();
    let ok = true;

    if (!String(nameInput.value || "").trim()) {
      setFieldError(nameInput, nameErrorEl, text.nameRequired);
      ok = false;
    }

    if (!String(phoneInput.value || "").trim()) {
      setFieldError(phoneInput, phoneErrorEl, text.phoneRequired);
      ok = false;
    } else if (!isPhoneNumberValid(PHONE_INSTANCE_KEY)) {
      setFieldError(phoneInput, phoneErrorEl, text.invalidPhone);
      ok = false;
    }

    if (!isValidEmailSyntax(emailInput.value)) {
      setFieldError(emailInput, emailErrorEl, text.invalidEmail);
      ok = false;
    }

    if (!ageInput.checked) {
      setFieldError(ageInput, ageErrorEl, text.ageRequired);
      ok = false;
    }

    if (!agreeInput.checked) {
      setFieldError(agreeInput, agreeErrorEl, text.agreementRequired);
      ok = false;
    }

    if (!isSignatureValid({ threshold: 0.004 })) {
      if (signatureErrorEl) {
        signatureErrorEl.textContent = text.signatureRequired;
      }
      ok = false;
    }

    return ok;
  }

  async function getCsrfToken() {
    const existing = String(
      (window.frappe && window.frappe.csrf_token) || ""
    ).trim();
    if (existing && existing !== "None") {
      return existing;
    }

    const response = await fetch("/api/method/zoni_edu.services.csrf_bootstrap", {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    const data = await response.json();
    const token = String(data?.message?.csrf_token || "").trim();
    window.frappe = window.frappe || {};
    window.frappe.csrf_token = token;
    return token;
  }

  async function postMethod(method, payload, { forceCap = false } = {}) {
    const csrfToken = await getCsrfToken();
    const headers = await capSolver.headers(
      {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Frappe-CSRF-Token": csrfToken,
      },
      { force: forceCap }
    );

    const response = await fetch(`/api/method/${method}`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(payload),
    });

    let data = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      throw userSafeError(extractFrappeErrorMessage(data) || text.genericError);
    }

    return data?.message || data || {};
  }

  function buildPayload() {
    return {
      full_name: String(nameInput.value || "").trim(),
      phone_number: getPhoneNumber(PHONE_INSTANCE_KEY),
      phone_country: getPhoneCountry(PHONE_INSTANCE_KEY),
      email: String(emailInput.value || "").trim(),
      is_18_or_older: ageInput.checked ? 1 : 0,
      consent_agreed: agreeInput.checked ? 1 : 0,
      signature_data_url: getSignatureDataUrl(),
      language: text.language,
    };
  }

  clearButton.addEventListener("click", () => {
    clearSignature();
    if (signatureErrorEl) {
      signatureErrorEl.textContent = "";
    }
  });

  clearListButton.addEventListener("click", () => {
    if (window.confirm(text.clearListConfirm)) {
      writeLocalSubmissions([]);
      renderLocalSubmissions();
    }
  });

  submitAnotherButton.addEventListener("click", () => {
    resetFormForNextSubmission();
  });

  [nameInput, emailInput, ageInput, agreeInput].forEach((input) => {
    input.addEventListener("input", () => {
      input.classList.remove("is-invalid");
    });
    input.addEventListener("change", () => {
      input.classList.remove("is-invalid");
    });
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    clearError();

    await phoneInitPromise;
    if (!(await validateBeforeSubmit())) {
      return;
    }

    const payload = buildPayload();
    const submittedName = payload.full_name;
    setSubmitting(true);

    try {
      const result = await postMethod(
        "zoni_edu.zoni_edu.doctype.zoni_media_consent_release.zoni_media_consent_release.submit_media_consent",
        payload,
        { forceCap: false }
      );

      if (result?.ok) {
        appendLocalSubmission({
          name: submittedName,
          recordName: result.name,
        });
        showConfirmation({
          name: submittedName,
          message: result.message,
        });
        form.reset();
        clearSignature();
      } else {
        showError(result?.message || text.genericError);
      }
    } catch (error) {
      showError(
        error.userSafe
          ? error.message
          : text.networkError || text.genericError
      );
    } finally {
      setSubmitting(false);
    }
  });

  renderLocalSubmissions();
})();
